import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AdminUser {
  id: string;
  name: string | null;
  display_name: string | null;
  email: string | null;
  email_confirmed: boolean;
  is_premium: boolean | null;
  is_evolver_founder: boolean | null;
  current_tier: string | null;
  total_workouts: number;
  days_active: number | null;
  created_at: string | null;
  avatar_url: string | null;
  referral_code: string | null;
  last_sign_in: string | null;
}

interface ListUsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const useAdminUsers = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchUsers = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    setError(null);

    try {
      // Build URL with query params
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...(search && { search }),
      });
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;

      if (!token) throw new Error('Not authenticated');

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-list-users?${params.toString()}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to fetch users');
      }

      const responseData: ListUsersResponse = await res.json();

      setUsers(responseData.users);
      setTotalUsers(responseData.total);
      setCurrentPage(responseData.page);
      setTotalPages(responseData.totalPages);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  const togglePremium = useCallback(async (userId: string, grantPremium: boolean, reason?: string, cancelStripeSubscription?: boolean) => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-toggle-premium', {
        body: { userId, grantPremium, reason, cancelStripeSubscription },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, is_premium: grantPremium } : user
      ));

      return { success: true, data };
    } catch (err) {
      console.error('Error toggling premium:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Failed to toggle premium' };
    }
  }, []);

  const exportToCsv = useCallback(async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;

      if (!token) throw new Error('Not authenticated');

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-list-users?export=csv`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error('Failed to export users');
      }

      // Download the CSV
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true };
    } catch (err) {
      console.error('Error exporting users:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Failed to export' };
    }
  }, []);

  return {
    users,
    loading,
    error,
    totalUsers,
    currentPage,
    totalPages,
    fetchUsers,
    togglePremium,
    exportToCsv,
  };
};
